import sanitizeHtmlLib from 'sanitize-html';
import type { CartItem, Coupon, CheckoutPaymentMethod, ProductSummary } from '@vastra/types';
import { isExternalHttpHref } from './inAppLinks';

export const classNames = (...classes: (string | boolean | undefined | null)[]): string =>
  classes.filter(Boolean).join(' ');

export const debounce = <T extends (...args: unknown[]) => unknown>(fn: T, delay: number) => {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};

export const truncate = (str: string, length: number): string =>
  str.length > length ? `${str.slice(0, length)}...` : str;

export const formatDate = (dateStr: string, locale = 'en-IN'): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const formatDateShort = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const formatCurrency = (amount: number, symbol = '₹'): string =>
  `${symbol}${amount.toLocaleString('en-IN')}`;

export const getImageUrl = (key: string, baseUrl?: string): string => {
  if (key.startsWith('http')) return key;
  const base = baseUrl || process.env.REACT_APP_CDN_URL || '';
  return `${base}/${key}`;
};

/** Extract YouTube video ID from watch or youtu.be URL. */
export const getYouTubeVideoId = (url: string): string | null => {
  if (!url) return null;
  try {
    if (url.includes('youtube.com/watch?v=')) {
      const id = url.split('v=')[1]?.split('&')[0];
      return id && id.length > 0 ? id : null;
    }
    if (url.includes('youtu.be/')) {
      const id = url.split('youtu.be/')[1]?.split('?')[0];
      return id && id.length > 0 ? id : null;
    }
  } catch {
    return null;
  }
  return null;
};

/** YouTube embed URL for iframe. */
export const getYouTubeEmbedUrl = (url: string): string | null => {
  const videoId = getYouTubeVideoId(url);
  return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
};

/** Thumbnail image URL for YouTube video (mqdefault = 320x180). */
export const getYouTubeThumbnailUrl = (url: string): string | null => {
  const videoId = getYouTubeVideoId(url);
  return videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null;
};

export const isInstagramUrl = (url: string): boolean =>
  url?.includes('instagram.com') ?? false;

export const getStockLabel = (stock: number): { text: string; color: string } => {
  if (stock <= 0) return { text: 'Out of Stock', color: 'text-red-700' };
  if (stock <= 3) return { text: `Only ${stock} left!`, color: 'text-red-600' };
  if (stock <= 10) return { text: `${stock} available`, color: 'text-amber-600' };
  return { text: 'In Stock', color: 'text-green-700' };
};

export const getOrderStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    Pending: 'bg-yellow-100 text-yellow-800',
    Confirmed: 'bg-blue-100 text-blue-800',
    Processing: 'bg-indigo-100 text-indigo-800',
    Shipped: 'bg-purple-100 text-purple-800',
    Delivered: 'bg-green-100 text-green-800',
    Cancelled: 'bg-red-100 text-red-800',
    Returned: 'bg-stone-100 text-stone-800',
    Exchanged: 'bg-amber-100 text-amber-800',
    Failed: 'bg-red-100 text-red-900',
  };
  return colors[status] || 'bg-stone-100 text-stone-600';
};

export const generateOrderId = (): string =>
  `VAS-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 5).toUpperCase()}`;

export const getWhatsAppUrl = (phone: string, message: string): string =>
  `https://wa.me/${phone.replace(/[^0-9]/g, '')}?text=${message}`;

export const validateEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const validatePhone = (phone: string): boolean =>
  /^[+]?[\d\s-]{10,15}$/.test(phone);

export const pluralize = (count: number, singular: string, plural?: string): string =>
  count === 1 ? singular : (plural || `${singular}s`);

export const scrollToTop = (): void => {
  if (typeof globalThis !== 'undefined' && 'scrollTo' in globalThis) {
    (globalThis as unknown as { scrollTo: (opts: { top: number; behavior: string }) => void }).scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }
};

const ALLOWED_TAGS = new Set([
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'br', 'hr', 'ul', 'ol', 'li',
  'a', 'strong', 'em', 'b', 'i', 'u', 'blockquote', 'code', 'pre', 'span',
  'div', 'img', 'figure', 'figcaption', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
]);
const ALLOWED_ATTRS = new Set(['href', 'src', 'alt', 'title', 'class', 'id', 'target', 'rel']);

/**
 * Sanitize HTML to prevent XSS attacks using sanitize-html.
 * Strips scripts, event handlers, dangerous protocols, and non-whitelisted tags/attributes.
 * Safe for server-side (Node) and client-side (browser) use.
 */
export const sanitizeHtml = (html: string): string => {
  if (!html || typeof html !== 'string') return '';
  
  return sanitizeHtmlLib(html, {
    allowedTags: Array.from(ALLOWED_TAGS),
    allowedAttributes: {
      '*': Array.from(ALLOWED_ATTRS),
      a: ['href', 'target', 'rel'],
      img: ['src', 'alt', 'title'],
    },
    disallowedTagsMode: 'discard',
    allowedSchemes: ['http', 'https', 'mailto', 'tel'],
    transformTags: {
      // Remove any onclick, onerror, etc handlers
      '*': (tagName: string, attribs: Record<string, string>) => {
        const cleanedAttribs: Record<string, string> = {};
        for (const [key, value] of Object.entries(attribs)) {
          if (!key.toLowerCase().startsWith('on') && typeof value === 'string') {
            cleanedAttribs[key] = value;
          }
        }
        return { tagName, attribs: cleanedAttribs };
      },
      // Same-site Quill links: stay in the SPA. External: new tab.
      a: (tagName: string, attribs: Record<string, string>) => {
        const cleanedAttribs: Record<string, string> = {};
        for (const [key, value] of Object.entries(attribs)) {
          if (!key.toLowerCase().startsWith('on') && typeof value === 'string') {
            cleanedAttribs[key] = value;
          }
        }
        const href = cleanedAttribs.href ?? '';
        if (isExternalHttpHref(href)) {
          cleanedAttribs.target = '_blank';
          cleanedAttribs.rel = 'noopener noreferrer';
        } else {
          delete cleanedAttribs.target;
          delete cleanedAttribs.rel;
        }
        return { tagName, attribs: cleanedAttribs };
      },
    },
  });
};

/** Plain text from HTML (search, previews, meta). Safe for Node and browser. */
export const stripHtmlToText = (html: string): string => {
  if (!html) return '';
  const withoutTags = html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p>/gi, ' ')
    .replace(/<[^>]+>/g, ' ');
  const decoded = withoutTags
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#(\d+);/g, (match, n: string) => {
      const code = Number(n);
      return code > 0 && code < 0x11_0000 ? String.fromCodePoint(code) : match;
    })
    .replace(/&#x([\da-fA-F]+);/g, (match, h: string) => {
      const code = parseInt(h, 16);
      return code > 0 && code < 0x11_0000 ? String.fromCodePoint(code) : match;
    });
  return decoded.replace(/\s+/g, ' ').trim();
};

/** True if current time is within optional [startsAt, endsAt] (ISO 8601). Missing bound = open. */
export const isPromoWindowActive = (startsAt?: string, endsAt?: string): boolean => {
  const now = Date.now();
  if (startsAt) {
    const t = Date.parse(startsAt);
    if (Number.isFinite(t) && now < t) return false;
  }
  if (endsAt) {
    const t = Date.parse(endsAt);
    if (Number.isFinite(t) && now > t) return false;
  }
  return true;
};

/** Active flag + optional expiry for checkout. */
export const isCouponCurrentlyValid = (c: { isActive: boolean; expiresAt?: string }): boolean => {
  if (!c.isActive) return false;
  if (c.expiresAt) {
    const t = Date.parse(c.expiresAt);
    if (Number.isFinite(t) && Date.now() > t) return false;
  }
  return true;
};

/** Checkout payment options in admin (storefront checkout is Paytm for Business only). */
export const CHECKOUT_PAYMENT_METHODS: CheckoutPaymentMethod[] = ['paytm'];

/** True if coupon's paymentMethods list permits the given checkout method. Empty/absent = allow all. */
export const couponAllowsPaymentMethod = (
  coupon: Pick<Coupon, 'paymentMethods'>,
  method: CheckoutPaymentMethod
): boolean => {
  const list = coupon.paymentMethods;
  if (!list || list.length === 0) return true;
  return list.includes(method);
};

/**
 * Cart line items that count toward the coupon discount (category + product-id rules).
 * An item qualifies if it matches product ID filter (when set) AND category filter (when set).
 */
export function getCouponEligibleCartLines(cart: CartItem[], coupon: Coupon): CartItem[] {
  const cat = coupon.applicableCategories;
  const ids = coupon.applicableProductIds;
  const hasCats = cat && cat.length > 0;
  const hasIds = ids && ids.length > 0;

  return cart.filter((item) => {
    if (hasIds && !ids!.includes(item.productId)) return false;
    if (hasCats && (!item.category || !cat!.includes(item.category))) return false;
    return true;
  });
}

/** Subtotal (₹) of cart lines eligible for this coupon. */
export function getCouponApplicableSubtotal(cart: CartItem[], coupon: Coupon): number {
  return getCouponEligibleCartLines(cart, coupon).reduce((sum, item) => sum + item.price * item.quantity, 0);
}

export function computeCouponDiscountAmount(applicableSubtotal: number, coupon: Coupon): number {
  if (applicableSubtotal <= 0) return 0;
  if (coupon.discountType === 'percentage') {
    return Math.round((applicableSubtotal * coupon.value) / 100);
  }
  return Math.min(coupon.value, applicableSubtotal);
}

export type CouponEligibilityReason =
  | 'ok'
  | 'inactive_or_expired'
  | 'wrong_payment_method'
  | 'below_min_purchase'
  | 'no_eligible_lines';

/** Full checkout eligibility for a coupon (cart + payment). */
export function getCouponCheckoutEligibility(
  cart: CartItem[],
  coupon: Coupon,
  paymentMethod: CheckoutPaymentMethod,
  isValid = isCouponCurrentlyValid(coupon)
): { ok: boolean; reason: CouponEligibilityReason; applicableSubtotal: number; discount: number } {
  if (!isValid) {
    return { ok: false, reason: 'inactive_or_expired', applicableSubtotal: 0, discount: 0 };
  }
  if (!couponAllowsPaymentMethod(coupon, paymentMethod)) {
    return { ok: false, reason: 'wrong_payment_method', applicableSubtotal: 0, discount: 0 };
  }
  const applicableSubtotal = getCouponApplicableSubtotal(cart, coupon);
  if (
    (coupon.applicableCategories?.length || coupon.applicableProductIds?.length) &&
    applicableSubtotal === 0
  ) {
    return { ok: false, reason: 'no_eligible_lines', applicableSubtotal: 0, discount: 0 };
  }
  if (applicableSubtotal < coupon.minPurchase) {
    return { ok: false, reason: 'below_min_purchase', applicableSubtotal, discount: 0 };
  }
  const discount = computeCouponDiscountAmount(applicableSubtotal, coupon);
  return { ok: true, reason: 'ok', applicableSubtotal, discount };
}

/** Catalog products that can use this coupon (for /coupon/:code page). */
export function filterProductsEligibleForCoupon(products: ProductSummary[], coupon: Coupon): ProductSummary[] {
  const cat = coupon.applicableCategories;
  const ids = coupon.applicableProductIds;
  const hasCats = cat && cat.length > 0;
  const hasIds = ids && ids.length > 0;
  if (!hasCats && !hasIds) return products;
  return products.filter((p) => {
    if (p.isSoftDeleted || p.isPublished === false) return false;
    if (hasIds && !ids!.includes(p.id)) return false;
    if (hasCats && (!p.category || !cat!.includes(p.category))) return false;
    return true;
  });
}

export { normalizeStaticPageHtmlWhitespace, normalizeStoreStaticPagesWhitespace } from './htmlWhitespace';
export { resolveInAppNavigationPath, isExternalHttpHref } from './inAppLinks';
export {
  normalizePromoBanners,
  resolveMaxActiveBanners,
  selectActivePromoBanners,
  isBannerActiveOnPage,
} from './promoBanners';
export {
  matchShippingRule,
  shippingAmountForRule,
  normalizeIndiaPincode,
  normalizeStateKey,
  normalizeCountryCodeForShipping,
} from './shippingResolve';

export { orderMilestoneRows } from './orderTimeline';
export {
  hasProcessedReturnExchange,
  displayOrderStatusForUi,
  displayPaymentStatusForUi,
} from './orderDisplayStatus';

/**
 * Get the UTC offset in minutes for a given IANA timezone at a specific instant.
 * Returns the difference such that: UTC + offset = local time in that zone.
 */
function getTimezoneOffsetMinutes(date: Date, tz: string): number {
  // Format the date parts in the target timezone
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parseInt(parts.find((p) => p.type === type)?.value ?? '0', 10);

  // Construct a date object as if the timezone parts were UTC
  const tzYear = get('year');
  const tzMonth = get('month') - 1;
  const tzDay = get('day');
  let tzHour = get('hour');
  // Intl may return 24 for midnight in some locales
  if (tzHour === 24) tzHour = 0;
  const tzMinute = get('minute');
  const tzSecond = get('second');

  const asUtc = Date.UTC(tzYear, tzMonth, tzDay, tzHour, tzMinute, tzSecond);
  return Math.round((asUtc - date.getTime()) / 60000);
}

/**
 * Convert ISO UTC string (e.g. 2026-09-19T07:51:00.000Z) to input value (YYYY-MM-DDTHH:mm).
 * When `tz` is provided, the output reflects the time in that IANA timezone instead of the browser's local zone.
 */
export function isoToDatetimeLocal(iso?: string, tz?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => n.toString().padStart(2, '0');

  if (tz) {
    const offsetMs = getTimezoneOffsetMinutes(d, tz) * 60000;
    const local = new Date(d.getTime() + offsetMs);
    return `${local.getUTCFullYear()}-${pad(local.getUTCMonth() + 1)}-${pad(local.getUTCDate())}T${pad(local.getUTCHours())}:${pad(local.getUTCMinutes())}`;
  }

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Convert input value (YYYY-MM-DDTHH:mm) to ISO UTC string (e.g. 2026-09-19T07:51:00.000Z).
 * When `tz` is provided, the input is interpreted as time in that IANA timezone instead of the browser's local zone.
 */
export function datetimeLocalToIso(localStr?: string, tz?: string): string | undefined {
  if (!localStr) return undefined;

  if (tz) {
    // Parse the YYYY-MM-DDTHH:mm string as components (not as a Date, which would apply browser TZ)
    const [datePart, timePart] = localStr.split('T');
    if (!datePart || !timePart) return undefined;
    const [y, m, day] = datePart.split('-').map(Number);
    const [h, min] = timePart.split(':').map(Number);
    if ([y, m, day, h, min].some((v) => v == null || isNaN(v))) return undefined;

    // Create a UTC date with these wall-clock values, then subtract the TZ offset to get the real UTC
    const asUtc = new Date(Date.UTC(y, m - 1, day, h, min, 0, 0));
    const offset = getTimezoneOffsetMinutes(asUtc, tz);
    // The offset may shift slightly around DST boundaries, so re-check with corrected time
    const corrected = new Date(asUtc.getTime() - offset * 60000);
    return corrected.toISOString();
  }

  const d = new Date(localStr);
  if (isNaN(d.getTime())) return undefined;
  return d.toISOString();
}
